import React from 'react';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './Dialog';
import { Button } from './Button';
import { Text } from './Text';
import { Badge } from './Badge';
import { PreCallPlan } from '../models/AppPreCallPlan';
import { PostCallPlan } from '../models/AppPostCallPlan';
import dateFormat from 'dateformat';

type PlanType = PreCallPlan | PostCallPlan;

interface PrintSummaryDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    plans: PlanType[];
    title: string;
    dateRange: string;
    loading?: boolean;
}

const PrintSummaryDialog: React.FC<PrintSummaryDialogProps> = ({
    isOpen, onClose, onConfirm, plans, title, dateRange, loading = false
}) => {
    const totalPlans = plans.length;

    // Check if these are pre-call plans or post-call plans
    const isPreCallPlan = (plan: PlanType): plan is PreCallPlan => {
        return 'key_message' in plan;
    };

    const completedPlans = plans.filter(plan => {
        if (isPreCallPlan(plan)) {
            return plan.post_call_plan_exist === '1';
        }
        return true; // Post-call plans are always considered completed
    }).length;

    const pendingPlans = totalPlans - completedPlans;

    const getUniqueSalesmen = () => {
        const salesmen = new Set(plans.map(plan => plan.salesman_username).filter(Boolean));
        return Array.from(salesmen);
    };

    const getUniqueCustomers = () => {
        const customers = new Set(plans.map(plan => plan.customer_fullname).filter(Boolean));
        return Array.from(customers);
    };

    const getPlanDate = (plan: PlanType) => {
        if (isPreCallPlan(plan)) {
            return plan.planned_date || plan.created_time;
        }
        return plan.created_time;
    };

    const getPlanStatus = (plan: PlanType) => {
        if (isPreCallPlan(plan)) {
            return plan.post_call_plan_exist === '1' ? 'Completed' : 'Pending';
        }
        return 'Completed'; // Post-call plans are always completed
    };

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogBody>
                <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <Text className="text-2xl font-bold text-blue-600">{totalPlans}</Text>
                                <Text className="text-sm text-gray-600">Total Plans</Text>
                            </div>
                            <div className="text-center">
                                <Text className="text-2xl font-bold text-green-600">{completedPlans}</Text>
                                <Text className="text-sm text-gray-600">Completed</Text>
                            </div>
                        </div>
                        <div className="mt-4 text-center">
                            <Text className="text-sm text-gray-600">Date Range: {dateRange}</Text>
                        </div>
                    </div>

                    {/* Salesmen and Customers */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Text className="font-semibold text-gray-900 mb-2">Salesmen ({getUniqueSalesmen().length})</Text>
                            <div className="space-y-1">
                                {getUniqueSalesmen().map((salesman, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                                        <Text className="text-sm">{salesman}</Text>
                                        <Badge color="blue" className="text-xs">
                                            {plans.filter(plan => plan.salesman_username === salesman).length}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Text className="font-semibold text-gray-900 mb-2">Customers ({getUniqueCustomers().length})</Text>
                            <div className="space-y-1">
                                {getUniqueCustomers().map((customer, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                                        <Text className="text-sm">{customer}</Text>
                                        <Badge color="green" className="text-xs">
                                            {plans.filter(plan => plan.customer_fullname === customer).length}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sample Plans Preview */}
                    <div>
                        <Text className="font-semibold text-gray-900 mb-2">Sample Plans (showing first 5)</Text>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {plans.slice(0, 5).map((plan, index) => (
                                <div key={plan.id} className="bg-white border border-gray-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <Text className="font-medium text-sm">{plan.customer_fullname}</Text>
                                            <Text className="text-xs text-gray-500">{plan.salesman_username}</Text>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                color={getPlanStatus(plan) === 'Completed' ? 'green' : 'yellow'}
                                                className="text-xs"
                                            >
                                                {getPlanStatus(plan)}
                                            </Badge>
                                            <Text className="text-xs text-gray-500">
                                                {dateFormat(getPlanDate(plan), "mmm dd")}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {plans.length > 5 && (
                                <Text className="text-sm text-gray-500 text-center py-2">
                                    ... and {plans.length - 5} more plans
                                </Text>
                            )}
                        </div>
                    </div>

                    {/* Warning/Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <Text className="text-sm text-blue-800">
                            This will generate a PDF report containing all {totalPlans} plans for the selected period.
                            The report will include customer details, salesmen, and completion status.
                        </Text>
                    </div>
                </div>
            </DialogBody>
            <DialogActions>
                <Button outline onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} disabled={loading}>
                    {loading ? 'Generating PDF...' : 'Generate PDF'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrintSummaryDialog;
